import React, { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import app from '../../services/firebase';
import TopNavbar from '../TopNavbar/TopNavbar';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ErrorBubble from '../ErrorBubble/ErrorBubble'; // Import the ErrorBubble component

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface Stat {
  caloriesConsumed: number;
  caloriesBurned: number;
  steps: number;
  dateString: string; // Add this property
  optionalFields?: Record<string, string>;
}

const StatsPage = () => {
  const [selectedDay, setSelectedDay] = useState<Date | undefined>();
  const [stats, setStats] = useState<Stat[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<number[]>([]);
  const [graphLabels, setGraphLabels] = useState<string[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<keyof Stat>('caloriesConsumed');
  const [granularity, setGranularity] = useState<string>('daily'); // Default to daily granularity

  const db = getFirestore(app);
  const auth = getAuth(app);

  // Fetch all data points for the logged-in user
  const fetchAllData = async () => {
    const user = auth.currentUser;
    if (!user) {
      setErrorMessage('You must be logged in to view stats.');
      return;
    }

    try {
      const querySnapshot = await getDocs(collection(db, 'users', user.uid, 'data'));

      if (querySnapshot.empty) {
        setErrorMessage('No data found for the user.');
        return;
      }

      const statsData = querySnapshot.docs.map((doc) => doc.data() as Stat);
      setStats(statsData);

      // Populate the graph with the default metric
      updateGraph(statsData, selectedMetric);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setErrorMessage('Error fetching stats. Please try again.');
    }
  };

  const aggregateData = (statsData: Stat[], metric: string, granularity: string) => {
    if (granularity === 'daily') {
      return statsData; // No aggregation needed for daily
    }

    const groupedData: { [key: string]: Stat[] } = {};

    statsData.forEach((stat) => {
      const date = new Date(stat.dateString);
      let key = '';

      if (granularity === 'weekly') {
        // Group by week (e.g., "2025-W18")
        const week = Math.ceil((date.getDate() - date.getDay() + 1) / 7);
        key = `${date.getFullYear()}-W${week}`;
      } else if (granularity === 'monthly') {
        // Group by month (e.g., "2025-05")
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      groupedData[key].push(stat);
    });

    // Aggregate data for each group
    const aggregatedStats = Object.entries(groupedData).map(([key, group]) => {
      const total = group.reduce(
        (acc, stat) => ({
          caloriesConsumed: acc.caloriesConsumed + stat.caloriesConsumed,
          caloriesBurned: acc.caloriesBurned + stat.caloriesBurned,
          steps: acc.steps + stat.steps,
        }),
        { caloriesConsumed: 0, caloriesBurned: 0, steps: 0 }
      );

      return {
        dateString: key,
        caloriesConsumed: total.caloriesConsumed / group.length,
        caloriesBurned: total.caloriesBurned / group.length,
        steps: total.steps / group.length,
      };
    });

    return aggregatedStats;
  };

  // Update the graph based on the selected metric
  const updateGraph = (statsData: Stat[], metric: keyof Stat) => {
    // Sort statsData by dateString in ascending order (earliest to latest)
    const sortedStats = statsData.sort((a, b) => {
      const dateA = new Date(a.dateString);
      const dateB = new Date(b.dateString);
      return dateA.getTime() - dateB.getTime(); // Compare timestamps
    });

    // Aggregate data based on granularity
    const aggregatedStats = aggregateData(sortedStats, metric, granularity);

    // Use short date format for labels
    const labels = aggregatedStats.map((stat) => {
      if (granularity === 'daily') {
        const date = new Date(stat.dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); // e.g., "May 7"
      }
      return stat.dateString; // Use the grouped key for weekly/monthly
    });

    // Map data for the selected metric
    const data = aggregatedStats.map((stat) => {
      // Ensure metric is one of the numeric keys
      if (metric === 'caloriesConsumed' || metric === 'caloriesBurned' || metric === 'steps') {
        return stat[metric]; // Safely index the metric
      }
      return 0; // Fallback if the metric is not valid
    });

    setGraphLabels(labels);
    setGraphData(data);
  };

  // Handle metric change from the dropdown menu
  const handleMetricChange = (metric: string) => {
    setSelectedMetric(metric as keyof Stat); // Cast to keyof Stat

    if (stats) {
      updateGraph(stats, metric as keyof Stat); // Ensure the correct type is passed
    }
  };

  // Fetch all data when the component mounts
  React.useEffect(() => {
    fetchAllData();
  }, []);

  React.useEffect(() => {
    if (stats && stats.length > 100 && granularity === 'daily') {
      setGranularity('monthly'); // Fallback to monthly if too many data points
      updateGraph(stats, selectedMetric);
    }
  }, [stats, granularity, selectedMetric]);

  React.useEffect(() => {
    if (stats) {
      updateGraph(stats, selectedMetric); // Update the graph when granularity changes
    }
  }, [granularity, stats, selectedMetric]);

  const chartData = {
    labels: graphLabels,
    datasets: [
      {
        label: selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1),
        data: graphData,
        borderColor:
          selectedMetric === 'caloriesConsumed'
            ? 'rgba(0, 128, 0, 1)' // Green for calories consumed
            : selectedMetric === 'caloriesBurned'
            ? 'rgba(255, 0, 0, 1)' // Red for calories burned
            : 'rgba(0, 0, 255, 1)', // Blue for steps
        backgroundColor:
          selectedMetric === 'caloriesConsumed'
            ? 'rgba(0, 128, 0, 0.2)' // Light green for calories consumed
            : selectedMetric === 'caloriesBurned'
            ? 'rgba(255, 0, 0, 0.2)' // Light red for calories burned
            : 'rgba(0, 0, 255, 0.2)', // Light blue for steps
        tension: 0.4, // Smooth the line
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false, // Allow the graph to grow larger
    plugins: {
      legend:
        {
          position: 'top' as const,
          labels: {
            font: {
              size: 14, // Modern font size for legend
            },
          },
        },
      title: {
        display: true,
        text: `Trend for ${selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}`,
        font: {
          size: 18, // Larger title font
          weight: 'bold' as 'bold', // Explicitly type "bold" to match Chart.js typings
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw} ${selectedMetric}`, // Add metric name to tooltip
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 12, // Modern font size for x-axis labels
          },
        },
      },
      y: {
        ticks: {
          font: {
            size: 12, // Modern font size for y-axis labels
          },
        },
      },
    },
  };

  return (
    <div>
      <TopNavbar />
      <div className="page-container">
        <h1>Stats Page</h1>
        <ErrorBubble message={errorMessage} type="error" />
        <div className="stats-page-container">
          {/* Left Section: Date Selector */}
          <div className="date-selector-container">
            <h2>Select a Date</h2>
            <DayPicker
              mode="single"
              selected={selectedDay}
              onSelect={(day) => setSelectedDay(day as Date)}
              disabled={{ after: new Date() }}
              fromDate={new Date(2025, 4, 1)}
            />
            {selectedDay && <p>Selected day: {selectedDay.toDateString()}</p>}
          </div>

          {/* Right Section: Stats and Graph */}
          <div className="graph-container">
            {selectedDay && (
              <>
                <h2>Stats for {selectedDay.toISOString().split('T')[0]}</h2>
                {stats && (
                  <div className="dynamic-stats">
                    {stats.filter((stat) => stat.dateString === selectedDay?.toISOString().split('T')[0]).length > 0 ? (
                      stats
                        .filter((stat) => stat.dateString === selectedDay?.toISOString().split('T')[0])
                        .map((stat, index) => (
                          <div key={index} className="dynamic-stat-item">
                            <p>
                              <strong>Calories Consumed:</strong> {stat.caloriesConsumed}
                            </p>
                            <p>
                              <strong>Calories Burned:</strong> {stat.caloriesBurned}
                            </p>
                            <p>
                              <strong>Steps:</strong> {stat.steps}
                            </p>
                            {stat.optionalFields && Object.keys(stat.optionalFields).length > 0 && (
                              <div>
                                <h3>Optional Fields:</h3>
                                <ul>
                                  {Object.entries(stat.optionalFields).map(([key, value]) => (
                                    <li key={key}>
                                      <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))
                    ) : (
                      <p className="no-data-message">There's no data for this date</p>
                    )}
                  </div>
                )}
              </>
            )}
            <h2>Trend Graph</h2>
            <div className="dropdown-container">
              <select
                id="granularity"
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)} // Only update the state
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
              <select
                value={selectedMetric}
                onChange={(e) => handleMetricChange(e.target.value as keyof Stat)} // Cast to keyof Stat
              >
                <option value="caloriesConsumed">Calories Consumed</option>
                <option value="caloriesBurned">Calories Burned</option>
                <option value="steps">Steps</option>
              </select>
            </div>
            <div className="graph-wrapper">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsPage;